// Film catalog (2026-07-30D) — public-profile renderer.
//
// History: M3/M5 shipped a FilmGrid with embedded YouTube iframes and
// /api/film-plays tracking. Kaillum's 2026-06-07 directive rolled that
// back to a hyperlink LIST (max 3). 2026-07-30D keeps the hyperlink-only
// rule and restores the GRID — up to 9 labelled clips, YouTube-home-page
// shape: thumbnail on top, title underneath, cards in a responsive grid.
//
// ⚠ STILL NO EMBEDS. A thumbnail <img> is fine; an <iframe> player is not.
// Nam's words: "I don't want to be another YouTube." `film_plays` stays an
// intentional orphan — nothing here writes to it.
//
// Thumbnails are DERIVED, never fetched: we parse the YouTube video id out
// of the stored URL and point an <img> at img.youtube.com. No API call, no
// key, no quota. Anything we can't parse (Drive, Hudl, IG, a malformed
// string) gets a neutral placeholder tile — never a broken-image glyph.
//
// Renders nothing when the films array is empty — main-column rule of
// thumb: don't reserve dead space for unpopulated sections.

import { useEffect, useState } from 'react'
import { Film, ExternalLink, Play } from 'lucide-react'

interface FilmRow {
  id: string
  url: string
  title: string | null
  created_at: string
}

// Hostname → friendly display name. Anything not in the map falls back
// to the bare hostname (with the leading `www.` stripped). The list is
// intentionally small — adding entries here doesn't break anything, but
// the fallback is already readable for unfamiliar hosts.
const HOST_DISPLAY: Record<string, string> = {
  'youtube.com': 'YouTube',
  'www.youtube.com': 'YouTube',
  'm.youtube.com': 'YouTube',
  'youtu.be': 'YouTube',
  'drive.google.com': 'Google Drive',
  'docs.google.com': 'Google Docs',
  'instagram.com': 'Instagram',
  'www.instagram.com': 'Instagram',
  'facebook.com': 'Facebook',
  'www.facebook.com': 'Facebook',
  'm.facebook.com': 'Facebook',
  'fb.watch': 'Facebook',
  'vimeo.com': 'Vimeo',
  'www.vimeo.com': 'Vimeo',
  'hudl.com': 'Hudl',
  'www.hudl.com': 'Hudl',
}

function hostLabel(rawUrl: string): string {
  try {
    const u = new URL(rawUrl)
    const mapped = HOST_DISPLAY[u.hostname]
    if (mapped) return mapped
    return u.hostname.replace(/^www\./, '')
  } catch {
    return 'External link'
  }
}

// --- YouTube thumbnail derivation ------------------------------------
//
// Video ids are exactly 11 chars of [A-Za-z0-9_-]. We validate the shape
// before building an image URL so a junk path segment can't produce a
// request for `img.youtube.com/vi//hqdefault.jpg`.
//
// The hostname gate matters: without it, any URL carrying a `?v=` query
// (a Vimeo link with tracking params, say) would render someone else's
// YouTube thumbnail. Parse the host first, THEN read the id.
const YT_HOSTS: ReadonlySet<string> = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
])
const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/
// Path forms that carry the id as the segment after a known prefix.
const YT_PATH_PREFIXES = ['shorts', 'embed', 'live', 'v'] as const

export function youtubeThumbnail(rawUrl: string): string | null {
  let u: URL
  try {
    u = new URL(rawUrl)
  } catch {
    return null
  }
  if (!YT_HOSTS.has(u.hostname)) return null

  const segments = u.pathname.split('/').filter(Boolean)
  let id: string | null = null

  if (u.hostname === 'youtu.be' || u.hostname === 'www.youtu.be') {
    // https://youtu.be/<ID>
    id = segments[0] ?? null
  } else if (segments[0] && (YT_PATH_PREFIXES as readonly string[]).includes(segments[0])) {
    // https://www.youtube.com/{shorts,embed,live,v}/<ID>
    id = segments[1] ?? null
  } else {
    // https://www.youtube.com/watch?v=<ID>
    id = u.searchParams.get('v')
  }

  if (!id || !YT_ID_RE.test(id)) return null
  // hqdefault is the one size guaranteed to exist for every video
  // (maxresdefault 404s on anything not uploaded in HD).
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`
}

// --- Card -------------------------------------------------------------
//
// Fallback ladder, mirroring Avatar.tsx: no derivable thumbnail OR a
// runtime load error → the placeholder tile. api-sports photos 404
// constantly and third-party thumbnails will do the same, so the error
// path is the expected path, not the exceptional one.
//
// ⚠ onError IS NOT ENOUGH ON ITS OWN, verified live 2026-07-30D. A
// well-formed but DEAD video id (deleted, private, or simply typo'd into
// a valid 11-char shape) does not 404 — img.youtube.com answers HTTP 200
// with a 120×90 grey sentinel image. onError never fires, and the grey
// blob gets stretched across a 16:9 card. So we ALSO check the decoded
// size on load: a real hqdefault is 480×360, the sentinel is 120×90.
// Anything at or under the sentinel width falls through to the
// placeholder. Probe used: id ZZZZZZZZZZZ → ok, 120×90.
const YT_SENTINEL_MAX_W = 120

function FilmCard({ film, index }: { film: FilmRow; index: number }) {
  const thumb = youtubeThumbnail(film.url)
  const [failed, setFailed] = useState(false)

  // Re-arm on url change so an edited row gets its own load attempt
  // instead of inheriting the previous URL's 404.
  useEffect(() => {
    setFailed(false)
  }, [film.url])

  const label = film.title?.trim() || `Film ${index + 1}`
  const host = hostLabel(film.url)
  const showThumb = thumb !== null && !failed

  return (
    <a
      href={film.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-lg border border-hoop-border bg-hoop-bg overflow-hidden hover:border-hoop-accent transition-colors"
    >
      <div className="relative aspect-video bg-hoop-surface">
        {showThumb ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            onLoad={(e) => {
              if (e.currentTarget.naturalWidth <= YT_SENTINEL_MAX_W) {
                setFailed(true)
              }
            }}
            className="w-full h-full object-cover"
          />
        ) : (
          // Neutral placeholder — decorative, so aria-hidden. The title
          // below always carries the accessible name of the link.
          <div
            aria-hidden="true"
            className="w-full h-full flex flex-col items-center justify-center gap-1 text-hoop-text-3"
          >
            <Film className="w-6 h-6" />
            <span className="text-[10px] uppercase tracking-wider">{host}</span>
          </div>
        )}
        {/* Play affordance. Decorative — this is a LINK OUT, not a player. */}
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30"
        >
          <Play className="w-8 h-8 text-white" fill="currentColor" />
        </span>
      </div>
      <div className="px-3 py-2.5">
        <p className="text-sm font-medium text-hoop-text line-clamp-2 group-hover:text-hoop-accent transition-colors">
          {label}
        </p>
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-hoop-text-3">
          {host}
          <ExternalLink className="w-3 h-3 shrink-0" />
        </p>
      </div>
    </a>
  )
}

export default function FilmSection({ films }: { films: FilmRow[] }) {
  if (films.length === 0) return null

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-5">
      <header className="flex items-center gap-2 mb-3">
        <Film className="w-3.5 h-3.5 text-hoop-text-3" />
        <h2 className="text-[12px] font-bold uppercase tracking-wider text-hoop-accent">
          Film
        </h2>
        <span className="text-[11px] text-hoop-text-3 font-normal normal-case tracking-normal">
          · {films.length}
        </span>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {films.map((f, i) => (
          <FilmCard key={f.id} film={f} index={i} />
        ))}
      </div>
    </section>
  )
}
